import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faExclamationTriangle,
  faFlagCheckered,
  faClock,
  faChartLine
} from '@fortawesome/free-solid-svg-icons';

// Base Modal Component
const BaseModal = ({ isOpen, onClose, children, theme }) => {
  const styles = {
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(5px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '1rem',
    },
    modal: {
      background: theme?.background?.card,
      borderRadius: '1.5rem',
      padding: '2.5rem',
      maxWidth: '500px',
      width: '100%',
      boxShadow: theme?.shadows?.xl,
      border: `1px solid ${theme?.borders?.primary}`,
      position: 'relative',
    },
    closeButton: {
      position: 'absolute',
      top: '1rem',
      right: '1rem',
      width: '2rem',
      height: '2rem',
      borderRadius: '0.5rem',
      border: 'none',
      background: theme?.backgrounds?.secondary,
      color: theme?.text?.secondary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          style={styles.overlay}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
        >
          <motion.div
            style={styles.modal}
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", damping: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            {onClose && (
              <button
                style={styles.closeButton}
                onClick={onClose}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Test Exit Modal
export const TestExitModal = ({ isOpen, onClose, onConfirm, theme }) => {
  const styles = {
    iconContainer: {
      width: '5rem',
      height: '5rem',
      borderRadius: '1rem',
      background: `linear-gradient(135deg, ${theme?.semantic?.warning}20, ${theme?.semantic?.warning}40)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1.5rem',
    },
    icon: {
      fontSize: '2.5rem',
      color: theme?.semantic?.warning,
    },
    title: {
      fontSize: '1.75rem',
      fontWeight: '700',
      color: theme?.text?.primary,
      textAlign: 'center',
      marginBottom: '0.75rem',
    },
    description: {
      fontSize: '1rem',
      color: theme?.text?.secondary,
      textAlign: 'center',
      lineHeight: 1.6,
      marginBottom: '2rem',
    },
    buttonContainer: {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'center',
    },
    button: {
      padding: '0.75rem 2rem',
      borderRadius: '0.75rem',
      border: 'none',
      fontSize: '0.875rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    primaryButton: {
      background: theme?.dashboard?.status?.incorrect,
      color: 'white',
    },
    secondaryButton: {
      background: theme?.backgrounds?.secondary,
      color: theme?.text?.primary,
      border: `1px solid ${theme?.borders?.primary}`,
    },
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} theme={theme}>
      <motion.div
        style={styles.iconContainer}
        animate={{ 
          rotate: [0, -5, 5, 0],
        }}
        transition={{ 
          duration: 2,
          repeat: Infinity,
          repeatDelay: 1,
        }}
      >
        <FontAwesomeIcon icon={faExclamationTriangle} style={styles.icon} />
      </motion.div>
      
      <h2 style={styles.title}>Exit Test?</h2>
      <p style={styles.description}>
        Are you sure you want to exit the test? Your progress will not be saved.
      </p>
      
      <div style={styles.buttonContainer}>
        <motion.button
          style={{ ...styles.button, ...styles.secondaryButton }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onClose}
        >
          Cancel
        </motion.button>
        <motion.button
          style={{ ...styles.button, ...styles.primaryButton }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onConfirm}
        >
          Yes, Exit
        </motion.button>
      </div>
    </BaseModal>
  );
};

// Test End Modal
export const TestEndModal = ({ isOpen, onClose, onConfirm, theme, unansweredCount = 0 }) => {
  const styles = {
    iconContainer: {
      width: '5rem',
      height: '5rem',
      borderRadius: '1rem',
      background: `linear-gradient(135deg, ${theme?.primary?.blue?.[100]}20, ${theme?.primary?.blue?.[200]}30)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1.5rem',
    },
    icon: {
      fontSize: '2.5rem',
      color: theme?.primary?.blue?.[600],
    },
    title: {
      fontSize: '1.75rem',
      fontWeight: '700',
      color: theme?.text?.primary,
      textAlign: 'center',
      marginBottom: '0.75rem',
    },
    description: {
      fontSize: '1rem',
      color: theme?.text?.secondary,
      textAlign: 'center',
      lineHeight: 1.6,
      marginBottom: '2rem',
    },
    highlight: {
      color: theme?.primary?.blue?.[600],
      fontWeight: '600',
    },
    warning: {
      fontSize: '0.9375rem',
      fontWeight: '600',
      color: theme?.semantic?.warning,
      textAlign: 'center',
      lineHeight: 1.5,
      marginBottom: '1.25rem',
      padding: '0.75rem 1rem',
      borderRadius: '0.75rem',
      background: `${theme?.semantic?.warning}1a`,
    },
    buttonContainer: {
      display: 'flex',
      gap: '1rem',
      justifyContent: 'center',
    },
    button: {
      padding: '0.75rem 2rem',
      borderRadius: '0.75rem',
      border: 'none',
      fontSize: '0.875rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    primaryButton: {
      background: `linear-gradient(135deg, ${theme?.primary?.blue?.[500]}, ${theme?.primary?.blue?.[600]})`,
      color: 'white',
    },
    secondaryButton: {
      background: theme?.backgrounds?.secondary,
      color: theme?.text?.primary,
      border: `1px solid ${theme?.borders?.primary}`,
    },
  };

  return (
    <BaseModal isOpen={isOpen} onClose={onClose} theme={theme}>
      <motion.div
        style={styles.iconContainer}
        animate={{ 
          scale: [1, 1.1, 1],
        }}
        transition={{ 
          duration: 2,
          repeat: Infinity,
        }}
      >
        <FontAwesomeIcon icon={faFlagCheckered} style={styles.icon} />
      </motion.div>
      
      <h2 style={styles.title}>End Test?</h2>
      {unansweredCount > 0 && (
        <p style={styles.warning}>
          You have {unansweredCount} unanswered {unansweredCount === 1 ? 'question' : 'questions'}.
          {' '}They'll be scored as incorrect. Tip: if you don't want this attempt scored,
          {' '}exit instead of ending.
        </p>
      )}
      <p style={styles.description}>
        If you choose to end the test now, your score will be calculated and you'll be redirected
        to the test performance page.
      </p>
      
      <div style={styles.buttonContainer}>
        <motion.button
          style={{ ...styles.button, ...styles.secondaryButton }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onClose}
        >
          Continue Test
        </motion.button>
        <motion.button
          style={{ ...styles.button, ...styles.primaryButton }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={onConfirm}
        >
          End Test
        </motion.button>
      </div>
    </BaseModal>
  );
};

// Test Time Up Modal
export const TestTimeUpModal = ({ isOpen, onConfirm, theme }) => {
  const styles = {
    iconContainer: {
      width: '5rem',
      height: '5rem',
      borderRadius: '1rem',
      background: `linear-gradient(135deg, ${theme?.dashboard?.status?.incorrect}20, ${theme?.dashboard?.status?.incorrect}40)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '0 auto 1.5rem',
    },
    icon: {
      fontSize: '2.5rem',
      color: theme?.dashboard?.status?.incorrect,
    },
    title: {
      fontSize: '1.75rem',
      fontWeight: '700',
      color: theme?.text?.primary,
      textAlign: 'center',
      marginBottom: '0.75rem',
    },
    description: {
      fontSize: '1rem',
      color: theme?.text?.secondary,
      textAlign: 'center',
      lineHeight: 1.6,
      marginBottom: '2rem',
    },
    statsContainer: {
      background: theme?.backgrounds?.secondary,
      borderRadius: '1rem',
      padding: '1.5rem',
      marginBottom: '2rem',
    },
    statRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      marginBottom: '0.5rem',
    },
    statIcon: {
      fontSize: '1rem',
      color: theme?.text?.tertiary,
    },
    statText: {
      fontSize: '0.875rem',
      color: theme?.text?.secondary,
    },
    button: {
      padding: '0.75rem 2rem',
      borderRadius: '0.75rem',
      border: 'none',
      fontSize: '0.875rem',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      background: `linear-gradient(135deg, ${theme?.primary?.teal?.[500]}, ${theme?.primary?.teal?.[600]})`,
      color: 'white',
      display: 'block',
      margin: '0 auto',
    },
  };

  return (
    <BaseModal isOpen={isOpen} onClose={null} theme={theme}>
      <motion.div
        style={styles.iconContainer}
        animate={{ 
          rotate: [0, -10, 10, -10, 10, 0],
        }}
        transition={{ 
          duration: 0.5,
          repeat: Infinity,
          repeatDelay: 2,
        }}
      >
        <FontAwesomeIcon icon={faClock} style={styles.icon} />
      </motion.div>
      
      <h2 style={styles.title}>Time's Up!</h2>
      <p style={styles.description}>
        The test time has expired. Your answers have been automatically saved.
      </p>
      
      <div style={styles.statsContainer}>
        <div style={styles.statRow}>
          <FontAwesomeIcon icon={faChartLine} style={styles.statIcon} />
          <span style={styles.statText}>
            Your test performance is ready for review
          </span>
        </div>
      </div>
      
      <motion.button
        style={styles.button}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={onConfirm}
      >
        View Results
      </motion.button>
    </BaseModal>
  );
};